// Note: Ignoring this rule because it is necessary for shorthand logic
/* eslint-disable no-unneeded-ternary */
import React, { BaseSyntheticEvent } from 'react';
import uuid from 'uuid';
import styled from 'styled-components';
import { darken } from 'polished';
//
import KeyboardDatePicker from 'components/date-pickers/keyboard';
import InputGroup from 'components/input/group';
import InputLabel from 'components/input/label';
import InputText from 'components/input/text';
import { InputError } from 'components/formula-entry/modals/_common';
import ButtonOutline from 'components/buttons/outline';
import ButtonText from 'components/buttons/text';
import { IDealProduct, EProductField, IPrice, IVolumeOption, IFieldOptionState } from 'store/field-options/types';
import availableVolumeUnits from 'data/volume-units';
import Select from 'components/input/select';
import MenuItem from 'components/menu-item';
import FormControl from 'components/input/form-control';
import colors from 'theme/colors';
import { EFormulaModalType } from 'store/formula-entry/types';
import { IDeal } from 'store/deals/types';
import FormulaModalButton from 'components/buttons/formula-modal';
import { productEndDateTestId, productStartDateTestId } from 'components/sidebar/base-navigation/test-ids';
import ProductMenuList from './product-list';

import {
  processFinancialCapCheckBox,
  processFinancialCapGeneralClick,
  processFinancialFloorCheckBox,
  processFinancialFloorGeneralClick,
  processVolumePushCheckBox,
  processVolumePushGeneralClick,
  processVolumePullCheckBox,
  processVolumePullGeneralClick,
  processVolumContractTermCheckBox,
  processVolumeContractTermGeneralClick,
} from './products-utility';

const { darkInputBorder, defaultBrowserOutlineBlue, darkBorder, lightBackgroundBase, white } = colors;

const darkenParamValue = 0.2;

const Product = styled.div({
  borderBottom: `1px solid ${darkBorder}`,
  borderRadius: 2,
  paddingTop: 16,
  paddingBottom: 16,
});

const RemoveButtonContainer = styled.div({
  textAlign: 'center',
});

const RemoveProductTextButton = styled(ButtonText)({
  color: lightBackgroundBase,
  textDecoration: 'underline',
  fontSize: 14,
  '&:hover': {
    color: darken(darkenParamValue, lightBackgroundBase),
  },
});

const ProductContainer = styled.div({
  paddingBottom: 16,
});

const NewProductButtonContainer = styled.div({
  paddingLeft: 16,
  paddingRight: 16,
  paddingTop: 16,
});

const NoProductsMessage = styled.p`
  text-align: center;
  font-size: 14px;
`;

const SectionHeading = styled.div`
  border-bottom: 1px solid ${darkBorder};
  border-top: 1px solid ${darkBorder};
  font-size: 14px;
  font-weight: 600;
  margin-top: 20px;
  padding-left: 16px;
  padding-bottom: 8px;
  padding-top: 8px;
`;

const OptionGroup = styled.div`
  margin-left: 20px;
  margin-right: 20px;
  margin-bottom: 20px;
`;

const CostsGroup = styled.div`
  margin-left: 20px;
  margin-right: 20px;
  margin-top: 50px;
  margin-bottom: 20px;
`;

const OptionLabel = styled.p`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Separator = styled.div`
  height: 8px;
`;

const DatePickerContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  .MuiFormControl-root {
    background: white;
    padding: 0px 4px;
    border: 1px solid ${darkInputBorder};
    border-radius: 2px;
  }

  .MuiInputBase-input {
    width: 55px;
    padding-top: 14px;
    padding-bottom: 14px;
  }

  .MuiInput-underline:after {
    border-color: ${defaultBrowserOutlineBlue};
  }

  .MuiInput-underline:before,
  .MuiInput-underline:hover:not(.Mui-disabled):before {
    border: none;
  }

  .MuiSvg-root {
    fill: ${white};
  }

  & > div:first-child {
    margin-right: 8px;
  }

  .MuiIconButton-root {
    padding: 0;
  }

  .MuiInputLabel-animated {
    color: ${white};
    font-weight: 600;
    position: relative;
    margin-bottom: 4px;
  }

  .MuiInputLabel-animated.Mui-focused {
    color: ${white};
  }

  label + .MuiInput-formControl {
    background-color: ${white};
    padding-left: 8px;
    padding-right: 4px;
    border-radius: 2px;
    margin-top: 0;
  }

  .MuiInputAdornment-positionEnd {
    margin-left: 4px;
  }
`;

const FormulaList = styled.p`
  font-size: 14px;
  text-decoration-line: underline;
  cursor: pointer;
`;

export interface IProducts {
  addNewProduct: React.MouseEventHandler<HTMLButtonElement> | undefined;
  dealEdit: IDeal;
  fieldOptions: IFieldOptionState;
  removeProduct: Function;
  startFormulaEdit: Function;
  updateProductField: Function;
  updateFieldOptions: Function;
  deleteProduct: Function;
}

const getIsBaseBatnaComplete = (batnaValue: string, priceValue: string) => {
  return !!batnaValue && !!priceValue;
};

const checkIsAdditionalBatnasComplete = (otherBatnas: IPrice[] | undefined) => {
  if (otherBatnas && otherBatnas.length > 0) {
    return true;
  }
  return false;
};

const checkParseFloatValue = (e: BaseSyntheticEvent) => {
  return e.target.value || 0;
};

const getCompleteValue = (extension: IVolumeOption | undefined, earlyTerm: IVolumeOption | undefined) => {
  return !!extension || !!earlyTerm;
};

const checkExtension = (extension: IVolumeOption | undefined) => {
  return extension?.enabled;
};

const checkEarlyTerm = (earlyTerm: IVolumeOption | undefined) => {
  return earlyTerm?.enabled;
};

const getEnabledValue = (extension: IVolumeOption | undefined, earlyTerm: IVolumeOption | undefined) => {
  if (checkExtension(extension) || checkEarlyTerm(earlyTerm)) {
    return true;
  }
  return false;
};

const checkPullValue = (pull: { enabled: boolean } | undefined) => {
  return pull?.enabled;
};

const checkPushValue = (push: { enabled: boolean } | undefined) => {
  return push?.enabled;
};

const checkMfgCostBatnaCostFixedIncInventoryTransitDays = (
  mfgCost: string | undefined,
  batnaCostFixed: number | undefined,
  batnaCostIncInventory: number | undefined,
  batnaCostTransitDays: number | undefined
) => {
  return mfgCost ?? batnaCostFixed ?? batnaCostIncInventory ?? batnaCostTransitDays;
};

const checkPriceCostFixedIncInventoryTransitDays = (
  priceCostFixed: number | undefined,
  priceCostIncInventory: number | undefined,
  priceCostTransitDays: number | undefined
) => {
  return priceCostFixed ?? priceCostIncInventory ?? priceCostTransitDays;
};

const checkPriceCostFreightReimbursedAndBatnaCostFreightReimbursed = (
  priceCostFreight: number,
  priceReimbursed: number,
  batnaCostFreight: number,
  batnaReimbursed: number
) => {
  return priceCostFreight || priceReimbursed || batnaCostFreight || batnaReimbursed;
};

export default ({
  addNewProduct,
  dealEdit,
  fieldOptions,
  removeProduct,
  startFormulaEdit,
  updateProductField,
  updateFieldOptions,
  deleteProduct,
}: IProducts) => {
  const { products, productLine } = dealEdit;
  const { products: availableProducts } = fieldOptions;

  return (
    <ProductContainer>
      {products ? (
        products.map((product: IDealProduct, index: number) => {
          // Flags
          const {
            capValue,
            capEnabled,
            floorValue,
            floorEnabled,
            push,
            pull,
            extension,
            earlyTerm,
            mainBatna,
            basePrice,
            otherBatnas,
            startDate,
            endDate,
            volume,
            volumeUnit,
            mfgCost,
            name,
          } = product;
          let isBaseBatnaComplete = false;
          let isAdditionalBatnasDisabled = false;
          let isAdditionalBatnasComplete = false;
          let isCostEnabled = false;
          if (mainBatna && basePrice) {
            const {
              formula: batnaValue,
              costFreight: batnaCostFreight,
              costReimbursed: batnaReimbursed,
              costFixed: batnaCostFixed,
              costIncInventory: batnaCostIncInventory,
              costTransitDays: batnaCostTransitDays,
            } = mainBatna;
            const {
              formula: priceValue,
              costFreight: priceCostFreight,
              costReimbursed: priceReimbursed,
              costFixed: priceCostFixed,
              costIncInventory: priceCostIncInventory,
              costTransitDays: priceCostTransitDays,
            } = basePrice;
            isBaseBatnaComplete = getIsBaseBatnaComplete(batnaValue, priceValue);
            isCostEnabled = !!(
              checkPriceCostFreightReimbursedAndBatnaCostFreightReimbursed(
                priceCostFreight,
                priceReimbursed,
                batnaCostFreight,
                batnaReimbursed
              ) ??
              checkMfgCostBatnaCostFixedIncInventoryTransitDays(
                mfgCost,
                batnaCostFixed,
                batnaCostIncInventory,
                batnaCostTransitDays
              ) ??
              checkPriceCostFixedIncInventoryTransitDays(priceCostFixed, priceCostIncInventory, priceCostTransitDays)
            );

            isAdditionalBatnasDisabled = !isBaseBatnaComplete;
            isAdditionalBatnasComplete = checkIsAdditionalBatnasComplete(otherBatnas);
          }

          return (
            <Product key={index}>
              <InputGroup>
                <InputLabel htmlFor={`product-id-${index}`}>PRODUCT {index + 1}</InputLabel>
                <FormControl variant="outlined">
                  <ProductMenuList
                    availableProducts={availableProducts}
                    index={index}
                    updateProductField={updateProductField}
                    updateFieldOptions={updateFieldOptions}
                    deleteProduct={deleteProduct}
                    name={name}
                    productLine={productLine}
                  />
                </FormControl>
              </InputGroup>

              <InputGroup>
                <InputLabel htmlFor={`input-volume-unit-${index}`}>UNIT</InputLabel>
                <FormControl variant="outlined">
                  <Select
                    id={`input-volume-unit-${index}`}
                    value={volumeUnit}
                    onChange={e => updateProductField(index, EProductField.VOLUME_UNIT, e.target.value)}
                    placeholder=""
                  >
                    {availableVolumeUnits.map((availableVolumeUnit: string) => (
                      <MenuItem key={`available-products-${uuid()}`} value={availableVolumeUnit}>
                        {availableVolumeUnit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </InputGroup>

              <InputGroup>
                <InputLabel htmlFor={`input-volume-value-${index}`}>MONTHLY VOLUME</InputLabel>
                <InputText
                  id={`input-volume-value-${index}`}
                  type="number"
                  value={volume}
                  onChange={e => updateProductField(index, EProductField.VOLUME, parseFloat(checkParseFloatValue(e)))}
                  placeholder=""
                />
              </InputGroup>

              <InputGroup>
                <DatePickerContainer>
                  <div>
                    <InputLabel>START</InputLabel>
                    <KeyboardDatePicker
                      clearable
                      views={['year', 'month']}
                      value={startDate}
                      format="MM/YYYY"
                      autoOk
                      InputLabelProps={{
                        shrink: true,
                      }}
                      onChange={date => updateProductField(index, EProductField.START_DATE, date)}
                      data-testid={productStartDateTestId}
                    />
                  </div>
                  <div>
                    <InputLabel>END</InputLabel>
                    <KeyboardDatePicker
                      clearable
                      views={['year', 'month']}
                      value={endDate}
                      format="MM/YYYY"
                      autoOk
                      InputLabelProps={{
                        shrink: true,
                      }}
                      onChange={date => updateProductField(index, EProductField.END_DATE, date)}
                      data-testid={productEndDateTestId}
                    />
                  </div>
                </DatePickerContainer>
                <InputError />
              </InputGroup>

              <Separator />

              <InputGroup>
                <FormulaModalButton
                  complete={isBaseBatnaComplete}
                  enabled={isBaseBatnaComplete}
                  label="Base & BATNA"
                  onGeneralClick={() => {
                    startFormulaEdit({
                      modalType: EFormulaModalType.BASE_BATNA,
                      productIndex: index,
                    });
                  }}
                />

                <FormulaModalButton
                  complete={isAdditionalBatnasComplete}
                  disabled={isAdditionalBatnasDisabled}
                  enabled={isAdditionalBatnasComplete}
                  label="Additional BATNAs"
                  onGeneralClick={() => {
                    startFormulaEdit({
                      modalType: EFormulaModalType.ADDITIONAL_BATNAS,
                      productIndex: index,
                    });
                  }}
                />
                <FormulaList
                  onClick={() => {
                    startFormulaEdit({
                      modalType: EFormulaModalType.FORMULA_LIST,
                      productIndex: index,
                    });
                  }}
                >
                  Formula list
                </FormulaList>
              </InputGroup>

              <SectionHeading>Options</SectionHeading>

              <OptionGroup>
                <OptionLabel>Financial</OptionLabel>
                <div>
                  <FormulaModalButton
                    complete={!!capValue}
                    enabled={!!capEnabled}
                    label="Cap"
                    onCheckboxClick={() => {
                      processFinancialCapCheckBox(isBaseBatnaComplete, capValue, index, capEnabled);
                    }}
                    onGeneralClick={() => {
                      processFinancialCapGeneralClick(isBaseBatnaComplete, index, startFormulaEdit);
                    }}
                  />
                  <FormulaModalButton
                    complete={!!floorValue}
                    enabled={!!floorEnabled}
                    label="Floor"
                    onCheckboxClick={() => {
                      processFinancialFloorCheckBox(isBaseBatnaComplete, floorValue, index, floorEnabled);
                    }}
                    onGeneralClick={() => {
                      processFinancialFloorGeneralClick(isBaseBatnaComplete, index, startFormulaEdit);
                    }}
                  />
                </div>
              </OptionGroup>

              <OptionGroup>
                <OptionLabel>Volume</OptionLabel>
                <div>
                  <FormulaModalButton
                    complete={!!push}
                    enabled={checkPushValue(push)}
                    label="Push"
                    onCheckboxClick={() => {
                      processVolumePushCheckBox(isBaseBatnaComplete, push, index);
                    }}
                    onGeneralClick={() => {
                      processVolumePushGeneralClick(isBaseBatnaComplete, index, startFormulaEdit);
                    }}
                  />
                  <FormulaModalButton
                    complete={!!pull}
                    enabled={checkPullValue(pull)}
                    label="Pull"
                    onCheckboxClick={() => {
                      processVolumePullCheckBox(isBaseBatnaComplete, pull, index);
                    }}
                    onGeneralClick={() => {
                      processVolumePullGeneralClick(isBaseBatnaComplete, index, startFormulaEdit);
                    }}
                  />
                  <FormulaModalButton
                    complete={getCompleteValue(extension, earlyTerm)}
                    enabled={getEnabledValue(extension, earlyTerm)}
                    label="Contract Term"
                    onCheckboxClick={() => {
                      processVolumContractTermCheckBox(isBaseBatnaComplete, extension, earlyTerm, index);
                    }}
                    onGeneralClick={() => {
                      processVolumeContractTermGeneralClick(isBaseBatnaComplete, index, startFormulaEdit);
                    }}
                  />
                </div>
              </OptionGroup>

              <CostsGroup>
                <FormulaModalButton
                  complete={isCostEnabled}
                  enabled={isCostEnabled}
                  label="Costs"
                  onGeneralClick={() => {
                    startFormulaEdit({
                      modalType: EFormulaModalType.COSTS,
                      productIndex: index,
                    });
                  }}
                />
              </CostsGroup>

              {products.length > 1 && (
                <RemoveButtonContainer>
                  <RemoveProductTextButton onClick={() => removeProduct(index, 'product')}>
                    Remove Product
                  </RemoveProductTextButton>
                </RemoveButtonContainer>
              )}
            </Product>
          );
        })
      ) : (
        <NoProductsMessage>No products</NoProductsMessage>
      )}

      <NewProductButtonContainer>
        <ButtonOutline onClick={addNewProduct}>Add Product</ButtonOutline>
      </NewProductButtonContainer>
    </ProductContainer>
  );
};
